import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Badge,
  Modal,
  Select,
  Skeleton,
  EmptyState,
  useToast,
} from '@bbpos/ui';
import { kitchenAssignmentService, type StationWorkload, type StationAssignment } from '@/services/kitchen-assignment.service';
import { userService } from '@/services/user.service';
import { locationService } from '@/services/location.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';

/**
 * G3.15 — Kitchen staff-to-station assignment page.
 *
 * - Lists every active KDS station at the current location as a card.
 * - Per-card: live staff chips + workload badge + "+ Assign" button.
 * - Top banner when any station is unassigned.
 * - Restaurant-ops palette per CLAUDE.md (violet/pink).
 */

const WAIT_COLOR = (seconds: number) => {
  if (seconds < 120) return { bg: 'bg-success-100', text: 'text-success-700', dark: 'dark:bg-success-900/30 dark:text-success-300' };
  if (seconds < 300) return { bg: 'bg-warning-100', text: 'text-warning-700', dark: 'dark:bg-warning-900/30 dark:text-warning-300' };
  return { bg: 'bg-danger-100', text: 'text-danger-700', dark: 'dark:bg-danger-900/30 dark:text-danger-300' };
};

function formatWait(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

export function StaffAssignmentPage() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);
  const profile = useAuthStore((s) => s.profile);

  // Explicit per-page location override so assignment never depends on the
  // header filter being on the right outlet. Falls back to the header / first
  // active location until the user picks one here.
  const headerLocationId = selectedLocationId ?? profile?.locations?.find((l) => l.is_active)?.id;
  const [pageLocationId, setPageLocationId] = useState<string | undefined>(undefined);
  const locationId = pageLocationId ?? headerLocationId;

  const locationsQuery = useQuery({
    queryKey: ['kitchen-staff-locations'],
    queryFn: () => locationService.list({ is_active: 'true', limit: 100 }),
    staleTime: 60_000,
  });
  const locationOptions = useMemo(
    () => (locationsQuery.data?.data ?? []).map((l) => ({ value: l.id, label: l.name })),
    [locationsQuery.data],
  );

  const [assigningStation, setAssigningStation] = useState<StationWorkload | null>(null);
  const [pickedUserId, setPickedUserId] = useState<string>('');

  // ── Queries ────────────────────────────────────────────────────────

  const workloadQuery = useQuery({
    queryKey: ['kitchen-workload', locationId],
    queryFn: () => kitchenAssignmentService.getWorkload(locationId!),
    enabled: !!locationId,
    refetchInterval: 15_000,
  });

  const unassignedQuery = useQuery({
    queryKey: ['kitchen-unassigned', locationId],
    queryFn: () => kitchenAssignmentService.getUnassigned(locationId!),
    enabled: !!locationId,
    refetchInterval: 15_000,
  });

  // Fetch all assignments at the location in one go so each card can pick its
  // own rows. The backend endpoint requires a stationId, so we fan out.
  const assignmentsQuery = useQuery({
    queryKey: ['kitchen-assignments-all', locationId, workloadQuery.data?.map((s) => s.station_id).join(',')],
    queryFn: async () => {
      if (!locationId || !workloadQuery.data) return [] as StationAssignment[];
      const lists = await Promise.all(
        workloadQuery.data.map((s) =>
          kitchenAssignmentService.listStationAssignments(s.station_id, locationId),
        ),
      );
      return lists.flat();
    },
    enabled: !!locationId && !!workloadQuery.data,
    refetchInterval: 15_000,
  });

  const staffQuery = useQuery({
    queryKey: ['assignable-staff', locationId],
    queryFn: () =>
      userService.list({
        location_id: locationId,
        is_active: 'true',
        limit: 100,
      }),
    enabled: !!locationId,
    staleTime: 60_000,
  });

  // ── Mutations ──────────────────────────────────────────────────────

  const assignMut = useMutation({
    mutationFn: ({ stationId, userId }: { stationId: string; userId: string }) =>
      kitchenAssignmentService.assign(stationId, { user_id: userId }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['kitchen-workload', locationId] });
      qc.invalidateQueries({ queryKey: ['kitchen-unassigned', locationId] });
      qc.invalidateQueries({ queryKey: ['kitchen-assignments-all', locationId] });
      setAssigningStation(null);
      setPickedUserId('');
      if (data.warnings.length > 0) {
        toast({ title: 'Assigned with warning', description: data.warnings[0], variant: 'warning' });
      } else {
        toast({ title: 'Staff assigned', variant: 'success' });
      }
    },
    onError: (err: Error) => {
      toast({ title: 'Assignment failed', description: err.message, variant: 'error' });
    },
  });

  const unassignMut = useMutation({
    mutationFn: (assignmentId: string) => kitchenAssignmentService.unassign(assignmentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kitchen-workload', locationId] });
      qc.invalidateQueries({ queryKey: ['kitchen-unassigned', locationId] });
      qc.invalidateQueries({ queryKey: ['kitchen-assignments-all', locationId] });
      toast({ title: 'Staff removed from station', variant: 'success' });
    },
    onError: (err: Error) => {
      toast({ title: 'Remove failed', description: err.message, variant: 'error' });
    },
  });

  // ── Derived state ──────────────────────────────────────────────────

  const assignmentsByStation = useMemo(() => {
    const map = new Map<string, StationAssignment[]>();
    for (const a of assignmentsQuery.data ?? []) {
      if (!map.has(a.station_id)) map.set(a.station_id, []);
      map.get(a.station_id)!.push(a);
    }
    return map;
  }, [assignmentsQuery.data]);

  const unassignedCount = unassignedQuery.data?.length ?? 0;

  // ── Render ────────────────────────────────────────────────────────

  return (
    <PageContainer>
      {/* Header — restaurant-ops gradient */}
      <div className="bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
        <h1 className="text-2xl font-bold text-white">Kitchen Station Staff</h1>
        <p className="text-violet-100 mt-1">
          Assign cooks and prep staff to stations for the current shift. Unassigned stations won’t receive orders.
        </p>
      </div>

      {/* Location selector — assignment is per outlet; assignable staff are the
          active users assigned to the chosen location. relative z-20 keeps the
          dropdown above the cards below. */}
      <div className="relative z-20 bg-white dark:bg-neutral-800 rounded-xl shadow-md ring-1 ring-neutral-100 dark:ring-neutral-700 p-4 mb-6 flex flex-wrap items-center gap-3">
        <div className="min-w-[220px]">
          <Select
            label="Location"
            options={locationOptions}
            value={locationId ?? ''}
            onChange={(v) => setPageLocationId(typeof v === 'string' ? v : (v[0] ?? undefined))}
            placeholder="Select a location"
          />
        </div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 flex-1 min-w-[200px]">
          Staff and stations shown are for this outlet. Assignable staff = active users assigned to this location (Users page).
        </p>
      </div>

      {!locationId ? (
        <EmptyState title="Pick a location" description="Choose a location above to view kitchen station assignments." />
      ) : (
      <>

      {/* Unassigned-stations banner */}
      {unassignedCount > 0 && (
        <div
          role="alert"
          className="bg-danger-50 border-l-4 border-danger-500 text-danger-800 p-4 rounded-md mb-6 dark:bg-danger-900/20 dark:text-danger-200 animate-slide-up"
          data-testid="unassigned-banner"
        >
          <p className="font-semibold">
            {unassignedCount} station{unassignedCount === 1 ? '' : 's'} unassigned
          </p>
          <p className="text-sm mt-1">
            Assign staff before opening the kitchen. Orders routed to these stations will sit in queue with no cook.
          </p>
        </div>
      )}

      {/* Station grid */}
      {workloadQuery.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      ) : (workloadQuery.data ?? []).length === 0 ? (
        <EmptyState
          title="No KDS stations"
          description="Create a station first before assigning staff."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(workloadQuery.data ?? []).map((st) => {
            const assignments = assignmentsByStation.get(st.station_id) ?? [];
            const waitColor = WAIT_COLOR(st.oldest_pending_age_seconds);
            const isUnassigned = st.staff_count === 0;
            return (
              <div
                key={st.station_id}
                className={`bg-white dark:bg-neutral-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 p-5 border ${
                  isUnassigned
                    ? 'border-danger-300 dark:border-danger-700'
                    : 'border-neutral-100 dark:border-neutral-700'
                } animate-scale-in`}
                data-testid={`station-card-${st.station_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold">
                      {st.station_type.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-neutral-900 dark:text-white">{st.station_name}</div>
                      <Badge variant="neutral" size="sm" className="mt-0.5">
                        {st.station_type}
                      </Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">Pending</div>
                    <div className="text-xl font-bold text-neutral-900 dark:text-white tabular-nums">
                      {st.pending_items}
                    </div>
                    {st.pending_items > 0 && (
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${waitColor.bg} ${waitColor.text} ${waitColor.dark}`}>
                        {formatWait(st.oldest_pending_age_seconds)}
                      </span>
                    )}
                  </div>
                </div>

                {isUnassigned && (
                  <div className="bg-danger-50 border-l-4 border-danger-500 text-danger-700 p-3 rounded text-sm mb-3 dark:bg-danger-900/20 dark:text-danger-200">
                    No staff assigned — orders will not be routed here.
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mb-3 min-h-[32px]">
                  {assignments.map((a) => (
                    <span
                      key={a.id}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-violet-100 text-violet-800 text-xs dark:bg-violet-900/30 dark:text-violet-200"
                      data-testid={`assignment-chip-${a.id}`}
                    >
                      {a.user_name}
                      <button
                        type="button"
                        aria-label={`Remove ${a.user_name}`}
                        onClick={() => unassignMut.mutate(a.id)}
                        className="ml-1 text-violet-600 hover:text-violet-900 dark:text-violet-300"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>

                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setAssigningStation(st);
                    setPickedUserId('');
                  }}
                  data-testid={`assign-button-${st.station_id}`}
                >
                  + Assign staff
                </Button>
              </div>
            );
          })}
        </div>
      )}
      </>
      )}

      {/* Assign modal */}
      <Modal
        open={!!assigningStation}
        onClose={() => setAssigningStation(null)}
        title={assigningStation ? `Assign to ${assigningStation.station_name}` : 'Assign'}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setAssigningStation(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (!assigningStation || !pickedUserId) return;
                assignMut.mutate({ stationId: assigningStation.station_id, userId: pickedUserId });
              }}
              disabled={!pickedUserId || assignMut.isPending}
              loading={assignMut.isPending}
            >
              Assign
            </Button>
          </div>
        }
      >
        <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-3">
          Only active staff are shown. The backend enforces that the selected user must also be clocked in for the
          current shift — if they aren&apos;t, the request will fail with a clear error.
        </p>
        {/* Native <select> — its option list is rendered by the OS as an
            overlay, so it never gets clipped/flipped inside this small modal
            (unlike the custom Select which overlapped the modal content). */}
        <label htmlFor="kitchen-staff-select" className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">Staff member</label>
        <select
          id="kitchen-staff-select"
          value={pickedUserId}
          onChange={(e) => setPickedUserId(e.target.value)}
          className="h-11 w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 px-3 text-sm text-neutral-900 dark:text-neutral-100 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none"
        >
          <option value="">— Select a staff member —</option>
          {(staffQuery.data?.data ?? []).map((u) => (
            <option key={u.id} value={u.id}>{`${u.name} (${u.role})`}</option>
          ))}
        </select>
        {staffQuery.isLoading && <p className="text-xs text-neutral-400 mt-1.5">Loading staff…</p>}
        {staffQuery.isError && (
          <p className="text-xs text-danger-600 mt-1.5">Couldn’t load staff: {(staffQuery.error as Error)?.message ?? 'request failed'}</p>
        )}
        {!staffQuery.isLoading && !staffQuery.isError && (staffQuery.data?.data ?? []).length === 0 && (
          <p className="text-xs text-warning-600 mt-1.5">
            No active staff are assigned to this location. Add users (or assign them to this outlet) under <strong>Users</strong>.
          </p>
        )}
      </Modal>
    </PageContainer>
  );
}

export default StaffAssignmentPage;
